/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { Form } from '../../molecules/PatternForm'
import StatusToast from 'components/molecules/StatusToast/StatusToast'
import { getAllChutes, getAllRules } from '../../../../../services/templateService'
import { Chute } from 'interfaces/templates'
import { useNavigate, useLocation } from 'react-router-dom'
import { useOperationModes } from 'core/hooks/useOperationModes'
import { OperationMode } from 'core/domain/operationMode'
import { useSaveTemplate } from '../../../hooks/useSaveTemplate'
import { Template } from '../../../../domain/entities/Template'
import { isDraft } from '../../../../domain/entities/TemplateStatus'
import { GetTemplateDetailUseCase } from '../../../../usecases/GetTemplateDetailUseCase'
import { AxiosTemplateRepository } from '../../../../infrastructure/AxiosTemplateRepository'
import { getApiErrorMessage } from 'utils/apiError'
import HeaderPageSection from 'core/presentation/components/molecules/HeaderPageSection/HeaderPageSection'
import AppButton from 'components/atoms/AppButton/AppButton'
import AppModalConfirmation from 'components/atoms/AppModalConfirmation/AppModalConfirmation'
import { LoaderTransparent } from 'components/molecules/LoaderTransparent/LoaderTransparent'

const breadcrumbItems = [
  { label: 'Plantillas', path: '/templates' },
  { label: 'Crear plantilla', path: '/templates/create' },
]

interface Rules {
  dndId: string;
  group_id?: number;
  chutes: number[];
  maxCapacity?: number | null;
  provider?: { name: string, code: string } | null;
  providerService?: { name: string, code: string } | null;
}

interface FormErrors {
  name?: string;
  operationMode?: string;
  rules?: string;
}

/** Tiempo que permanece visible el mensaje de confirmación antes de navegar al detalle. */
const NAVIGATION_DELAY_MS = 1200;

const templateRepository = new AxiosTemplateRepository();
const getTemplateDetailUseCase = new GetTemplateDetailUseCase(templateRepository);

export default function PatternCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateFrom = (location.state as { duplicateFrom?: number } | null)?.duplicateFrom;
  const { saveTemplate, loading: isSaving } = useSaveTemplate();
  const [loadingDuplicate, setLoadingDuplicate] = useState(duplicateFrom !== undefined);
  const [duplicateOriginLoaded, setDuplicateOriginLoaded] = useState(duplicateFrom === undefined);
  const [routeChangeLoading, setRouteChangeLoading] = useState<Record<number, boolean>>({});

  const [groups, setGroups] = useState<Rules[]>([{ dndId: `new-${crypto.randomUUID()}`, chutes: [], maxCapacity: null, provider: null, providerService: null }]);
  const [ruleOptions, setRuleOptions] = useState<{ value: string; label: string }[]>([]);
  const [baseChutes, setBaseChutes] = useState<Chute[]>([]);
  const [patternName, setPatternName] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [toast, setToast] = useState<{ title: string; type: 'success' | 'error' | 'loading'; visible: boolean }>({
    title: '',
    type: 'success',
    visible: false,
  });

  // Modo de operación
  const { operationModes, findById, findByCode } = useOperationModes();
  const [selectedOperationMode, setSelectedOperationMode] = useState<OperationMode | null>(null);
  const [pendingOperationModeCode, setPendingOperationModeCode] = useState<string | undefined>(undefined);

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const navigationTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (navigationTimeout.current) clearTimeout(navigationTimeout.current);
  }, []);

  // ── DnD sensors ──────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setGroups(prev => {
        const oldIndex = prev.findIndex(g => g.dndId === active.id);
        const newIndex = prev.findIndex(g => g.dndId === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const handleOperationModeChange = (code: string, id: number) => {
    const mode = findById(id);
    setSelectedOperationMode(mode);
    setErrors(prev => ({ ...prev, operationMode: undefined }));
  };

  const handleNameChange = (value: string) => {
    setPatternName(value);
    if (value.trim()) setErrors(prev => ({ ...prev, name: undefined }));
  };

  const showToast = (title: string, type: 'success' | 'error' | 'loading') => {
    setToast({ title, type, visible: true });
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 3000);
  };

  const handleAddRule = () => {
    setGroups(prev => [...prev, { dndId: `new-${crypto.randomUUID()}`, chutes: [], maxCapacity: null, provider: null, providerService: null }]);
    setErrors(prev => ({ ...prev, rules: undefined }));
  };

  const handleChutesSelected = (ruleIndex: number, selectedChuteNumbers: number[]) => {
    setGroups(prev => {
      const updated = [...prev];
      const selectedChuteIds = selectedChuteNumbers
        .map(num => baseChutes.find(c => c.chute === num)?.id)
        .filter((id): id is number => id !== undefined);
      updated[ruleIndex].chutes = selectedChuteIds;
      return updated;
    });
  };

  const handleDeleteRule = (dndId: string) => {
    setGroups(prev => prev.filter(g => g.dndId !== dndId));
  };

  const handleReorder = (fromIndex: number, toIndex: number) => {
    setGroups(prev => arrayMove(prev, fromIndex, toIndex));
  };

  const handleUpdateGroup = (ruleIndex: number, group_id: number, chutes: number[]) => {
    setGroups(prev => {
      const updated = [...prev];
      updated[ruleIndex] = { ...updated[ruleIndex], group_id, chutes };
      return updated;
    });
    setErrors(prev => ({ ...prev, rules: undefined }));
  };

  const handleUpdateRouteChange = (ruleIndex: number, routeChangeConfig: { provider: { name: string, code: string } | null; providerService: { name: string, code: string } | null }) => {
    setGroups(prev => {
      const updated = [...prev];
      updated[ruleIndex] = {
        ...updated[ruleIndex],
        provider: routeChangeConfig.provider,
        providerService: routeChangeConfig.providerService
      };
      return updated;
    });
  }

  const handleMaxCapacityChange = (ruleIndex: number, value: number | null) => {
    setGroups(prev => {
      const updated = [...prev];
      updated[ruleIndex] = { ...updated[ruleIndex], maxCapacity: value };
      return updated;
    });
  };

  /**
   * Valida los datos obligatorios de la plantilla.
   * La asignación de chutes es opcional: una plantilla sin chutes se guarda
   * como borrador y se completa más adelante.
   */
  const validateForm = (): FormErrors => {
    const formErrors: FormErrors = {};

    if (!patternName.trim()) {
      formErrors.name = 'El nombre de la plantilla es requerido';
      return formErrors;
    }

    if (!selectedOperationMode) {
      formErrors.operationMode = 'Debe seleccionar un modo de operación';
      return formErrors;
    }

    if (groups.length === 0) {
      formErrors.rules = 'Debe agregar al menos una regla a la plantilla';
      return formErrors;
    }

    const ruleWithoutType = groups.findIndex(rule => rule.group_id === undefined);
    if (ruleWithoutType >= 0) {
      formErrors.rules = `Debe seleccionar el tipo de regla en la regla ${ruleWithoutType + 1}`;
      return formErrors;
    }

    const ruleWithoutService = groups.findIndex(rule => !!rule.provider && !rule.providerService);
    if (ruleWithoutService >= 0) {
      formErrors.rules = `Debe seleccionar el servicio del proveedor en la regla ${ruleWithoutService + 1}`;
    }

    return formErrors;
  };

  const handleCreate = async () => {
    const formErrors = validateForm();
    setErrors(formErrors);

    const firstError = formErrors.name ?? formErrors.operationMode ?? formErrors.rules;
    if (firstError !== undefined) {
      // Se permanece en el formulario conservando los datos ya ingresados.
      return showToast(firstError, 'error');
    }

    try {
      showToast('Creando plantilla...', 'loading');

      // Build Domain Entity
      const domainTemplate: Template = {
        name: patternName.trim(),
        // validateForm() ya garantiza que el modo de operación está seleccionado.
        operationMode: selectedOperationMode ?? undefined,
        rules: groups.map(g => ({
          groupId: g.group_id as number,
          chutes: g.chutes
            .map(id => baseChutes.find(c => c.id === id)?.chute)
            .filter((chute): chute is number => chute !== undefined),
          maxCapacity: g.maxCapacity && g.maxCapacity > 0 ? g.maxCapacity : null,
          provider: g.provider || null,
          providerService: g.providerService || null
        }))
      };
      const savedTemplate = await saveTemplate(domainTemplate);

      setErrors({});
      showToast(
          isDraft(savedTemplate.status)
              ? 'Plantilla guardada como borrador'
              : 'Plantilla creada con éxito',
          'success',
      );
        // Se retrasa la navegación para que el mensaje de confirmación sea visible.
        navigationTimeout.current = setTimeout(() => {
            navigate(`/templates/show/${savedTemplate.id}`);
        }, NAVIGATION_DELAY_MS);
    } catch (error: any) {
        // Los datos ingresados se conservan para que el usuario pueda corregirlos.
        showToast(getApiErrorMessage(error, 'Error al crear la plantilla'), 'error');
    }
  };

  const renderRuleItems = () =>
    groups.map((group, index) => {
      const defaultChutes = group.chutes
        .map(chute => {
          const match = baseChutes.find(c => c.chute === chute);
          return match ? { chute: match.chute, id: match.id } : null;
        })
        .filter((c): c is { chute: number; id: number } => c !== null);

      const disabledRuleIds = groups
        .map(g => g.group_id)
        .filter((gid): gid is number => typeof gid === 'number')
        .filter((gid, i) => i !== index);

      const assignedChutes = groups.flatMap((g, i) => (i === index ? [] : g.chutes));

      return (
        <Form.RuleItem
          key={group.dndId}
          dndId={group.dndId}
          position={index + 1}
          totalCount={groups.length}
          chutes={baseChutes}
          ruleOptions={ruleOptions}
          onDelete={() => handleDeleteRule(group.dndId)}
          onReorder={handleReorder}
          ruleIndex={index}
          onChutesSelected={handleChutesSelected}
          selectedChutes={assignedChutes}
          onGroupUpdate={handleUpdateGroup}
          selectedRuleId={group.group_id?.toString()}
          defaultChutes={defaultChutes}
          disabledRuleIds={disabledRuleIds}
          showChutes={false}
          defaultRouteChangeConfig={{
            provider: group.provider || null,
            providerService: group.providerService || null,
          }}
          onRouteChangeUpdate={(config) => handleUpdateRouteChange(index, config)}
          onRouteChangeLoadingChange={(ruleIndex, loading) =>
            setRouteChangeLoading(prev =>
              prev[ruleIndex] === loading ? prev : { ...prev, [ruleIndex]: loading },
            )
          }
          maxCapacity={group.maxCapacity}
          onMaxCapacityChange={handleMaxCapacityChange}
        />
      );
    });

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const rulesResponse = await getAllRules();
        const options = rulesResponse?.groups?.map((group: { id: number; name: string }) => ({
          value: group.id.toString(),
          label: group.name,
        })) ?? [];
        setRuleOptions(options);
      } catch (err) {
        setRuleOptions([]);
      }
    };
    fetchRules();
  }, []);

  useEffect(() => {
    if (duplicateFrom === undefined) return;

    const fetchOriginTemplate = async () => {
      try {
        const originTemplate = await getTemplateDetailUseCase.execute(duplicateFrom);
        setPatternName(`${originTemplate.name} (Copia)`);
        setPendingOperationModeCode(originTemplate.operationMode?.code);
        setGroups(
          originTemplate.rules.map((rule, index) => ({
            dndId: `duplicate-${index}`,
            group_id: rule.groupId,
            chutes: [],
            maxCapacity: rule.maxCapacity ?? null,
            provider: rule.changeRoute === true ? rule.provider || null : null,
            providerService: rule.changeRoute === true ? rule.providerService || null : null,
          })),
        );
      } catch (err) {
        showToast('Error al cargar los datos de la plantilla de origen', 'error');
      } finally {
        setDuplicateOriginLoaded(true);
      }
    };
    fetchOriginTemplate();
  }, [duplicateFrom]);

  useEffect(() => {
    if (pendingOperationModeCode === undefined) return;
    const mode = findByCode(pendingOperationModeCode);
    if (mode) {
      setSelectedOperationMode(mode);
      setPendingOperationModeCode(undefined);
    }
  }, [pendingOperationModeCode, findByCode]);

  useEffect(() => {
    if (!duplicateOriginLoaded) return;

    const pendingRouteChangeIndexes = groups
      .map((group, index) => (group.provider ? index : null))
      .filter((index): index is number => index !== null);

    const stillResolvingRouteChange = pendingRouteChangeIndexes.some(
      index => routeChangeLoading[index] !== false,
    );

    setLoadingDuplicate(stillResolvingRouteChange);
  }, [duplicateOriginLoaded, groups, routeChangeLoading]);

  const formValidation = validateForm();
  const isFormComplete =
    formValidation.name === undefined &&
    formValidation.operationMode === undefined &&
    formValidation.rules === undefined;

  const isRulePristine = (rule: Rules) =>
    rule.group_id === undefined &&
    rule.chutes.length === 0 &&
    (rule.maxCapacity === null || rule.maxCapacity === undefined) &&
    rule.provider === null &&
    rule.providerService === null;

  const hasUnsavedChanges =
    patternName.trim() !== '' ||
    selectedOperationMode !== null ||
    groups.length !== 1 ||
    !isRulePristine(groups[0]);

  useEffect(() => {
    if (!selectedOperationMode) return;
    const fetchChutes = async () => {
      try {
        const chutesResponse = await getAllChutes();
        const chutes = chutesResponse.data?.default_chutes || [];
        const transformedChutes: Chute[] = chutes.map((item: any) => ({
          id: item.id,
          chute: item.number,
          type: 'available',
        }));
        setBaseChutes(transformedChutes);
      } catch {
        setBaseChutes([]);
      }
    };
    fetchChutes();
  }, [selectedOperationMode]);

  return (
    <>
      <HeaderPageSection
        title="Crear nueva plantilla"
        showGoBack={false}
        breadcrumbItems={breadcrumbItems}
        actionsAlign="end"
      >
        <div className="flex items-center gap-[8px]">
          <AppButton
            title="Descartar"
            onClick={() => (hasUnsavedChanges ? setShowDiscardConfirm(true) : navigate('/templates'))}
            state="ghost"
            size="md"
            className="!px-[20px] !text-[16px] !leading-[20px] !text-[#141a1f] font-semibold"
          />
          <AppButton
            title="Crear plantilla"
            onClick={handleCreate}
            disabled={isSaving || !isFormComplete}
            state="primary"
            className="!px-[20px] !text-[16px] !leading-[20px] font-semibold drop-shadow-[0px_3px_3px_rgba(148,163,184,0.15)] disabled:!text-[#cbd5e1] disabled:drop-shadow-none"
            data-testid="create-template-header-btn"
          />
        </div>
      </HeaderPageSection>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={groups.map(g => g.dndId)}
          strategy={verticalListSortingStrategy}
        >
          <Form
            onAddRule={handleAddRule}
            onCreateTemplate={handleCreate}
            onNameChange={handleNameChange}
            templateName={patternName}
            buttonDisabled={isSaving || !isFormComplete}
            operationModes={operationModes}
            selectedOperationMode={selectedOperationMode?.code || ''}
            onOperationModeChange={handleOperationModeChange}
            nameError={errors.name}
            operationModeError={errors.operationMode}
            rulesError={errors.rules}
            hideFooterActions
          >
            {renderRuleItems()}
          </Form>
        </SortableContext>
      </DndContext>

      {loadingDuplicate && <LoaderTransparent />}
      {toast.visible && <StatusToast title={toast.title} type={toast.type} />}

      <AppModalConfirmation
        isOpen={showDiscardConfirm}
        title="Descartar plantilla"
        description="Tienes cambios sin guardar en esta plantilla. ¿Estás seguro de que quieres descartarlos?"
        buttonCancelText="Seguir editando"
        buttonSubmitText="Cerrar y descartar"
        confirmVariant="primary"
        onClose={() => setShowDiscardConfirm(false)}
        handleConfirm={() => navigate('/templates')}
      />
    </>
  );
}
